import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { AlertTriangle } from 'lucide-react';

interface SyringeWarningDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  injectableMedications: string[];
  onAcknowledge: () => void;
  onGoBack: () => void;
}

const SyringeWarningDialog: React.FC<SyringeWarningDialogProps> = ({
  open,
  onOpenChange,
  injectableMedications,
  onAcknowledge,
  onGoBack,
}) => {
  const [acknowledged, setAcknowledged] = useState(false);

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setAcknowledged(false);
    }
    onOpenChange(newOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className='sm:max-w-[540px]'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div className='flex h-10 w-10 items-center justify-center rounded-full bg-amber-100'>
              <AlertTriangle className='h-5 w-5 text-amber-600' />
            </div>
            <div>
              <DialogTitle className='text-amber-900'>Syringe Not Included</DialogTitle>
              <p className='mt-0.5 text-sm text-amber-600'>
                No syringe found in this prescription
              </p>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4'>
          <p className='text-sm text-slate-700'>
            This prescription includes injectable medication(s) but does not include a syringe.
            Consider adding a syringe to the prescription.
          </p>

          {/* Injectable Medications List */}
          <div className='space-y-2'>
            {injectableMedications.map((medication, index) => (
              <div
                key={index}
                className='rounded-lg border border-amber-200 bg-amber-50 p-3'
              >
                <div className='flex items-start gap-2'>
                  <AlertTriangle className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-600' />
                  <p className='text-sm font-semibold text-amber-900'>{medication}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Acknowledgement Checkbox */}
          <div className='flex items-start gap-3 rounded-lg border border-slate-200 bg-slate-50 p-3'>
            <Checkbox
              id='syringe-acknowledge'
              checked={acknowledged}
              onCheckedChange={(checked) => setAcknowledged(checked === true)}
              className='mt-0.5'
            />
            <label
              htmlFor='syringe-acknowledge'
              className='cursor-pointer text-sm leading-snug text-slate-700'
            >
              I confirm that the patient does not need a syringe included with this prescription.
            </label>
          </div>
        </div>

        <DialogFooter className='gap-2 sm:gap-0'>
          <Button
            variant='outline'
            onClick={() => {
              setAcknowledged(false);
              onGoBack();
            }}
          >
            Go Back
          </Button>
          <Button
            variant='default'
            className='bg-amber-600 hover:bg-amber-700 text-white'
            onClick={() => {
              setAcknowledged(false);
              onAcknowledge();
            }}
            disabled={!acknowledged}
          >
            Proceed Anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyringeWarningDialog;
